import type { AssetStatus } from './assets';
import type { AttachmentDetail } from './attachments';
import { fetchPage, patchResource, type ListParams, type PageResponse } from './collections';
import { apiFetch } from './http';
import type { CommentDetail, EntityReference, PriorityReference } from './requests';
import type { SlaSummaryState } from './sla';
import type { TimeEntryWritten } from './timeTracking';

/**
 * Tickets (07 §4.7, 09 §5). The state machine is the tenant's: the console never hardcodes a
 * status, it offers the edges the API resolved from `status_transitions` (`D-WF-01`).
 */

/** The six categories the engine branches on (`CONV-44`); the names beside them are the tenant's. */
export const STATUS_CATEGORIES = [
  'new',
  'open',
  'pending',
  'resolved',
  'closed',
  'cancelled',
] as const;

export type StatusCategory = (typeof STATUS_CATEGORIES)[number];

/**
 * `AM-302`: the categories of a ticket that may still be replaced by a new one. A resolved one is
 * reopened first; a closed or cancelled one is not replaced (the server answers
 * `ticket_not_replaceable`).
 */
export const REPLACEABLE_CATEGORIES: readonly StatusCategory[] = ['new', 'open', 'pending'];

/** `AM-889`: the fifth, `maintenance`, is the planned work (the plans' tickets and the provider's own). */
export const TICKET_TYPES = [
  'service_request',
  'incident',
  'problem',
  'change',
  'maintenance',
] as const;

export type TicketType = (typeof TICKET_TYPES)[number];

/** `IncidentSeverities.All` (`ck_incident_details_severity`): sev1 is the worst, sev4 the least. */
export const INCIDENT_SEVERITIES = ['sev1', 'sev2', 'sev3', 'sev4'] as const;

export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

/** The two roles the selector of 05e §2.3 offers a linked asset (`AM-128`). */
export const TICKET_ASSET_ROLES = ['affected', 'used'] as const;

export type TicketAssetRole = (typeof TICKET_ASSET_ROLES)[number];

export interface StatusReference {
  id: string;
  code: string;
  name: string;
  category: StatusCategory;
}

/**
 * The promise on a row of `GET /tickets` (`AM-135` point 3): the state the server decided over the
 * clocks of the cycle in force, and the deadline it is measured against. `dueAt` is null for a
 * promise already met - there is nothing left to count down to - and an instant in the other four.
 * The whole field is null when the ticket has no clock in force at all (no policy applies, or every
 * clock was cancelled), which is why the console never reads absence as "on track".
 */
export interface TicketSlaSummary {
  state: SlaSummaryState;
  dueAt?: string | null;
}

export interface TicketSummary {
  id: string;
  ticketNumber: string;
  ticketType: TicketType;
  title: string;
  status: StatusReference;
  priority: PriorityReference;
  organization: EntityReference;
  /** Always present (`AM-208`): the ticket's own rubro, never a ticket without one. */
  serviceArea: EntityReference;
  /** `AM-213`: optional so the fixtures of before this feature stay valid; absent, no location was named. */
  location?: EntityReference | null;
  requesterPerson?: EntityReference | null;
  assignedTeam?: EntityReference | null;
  assignedAgent?: EntityReference | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The later of `updatedAt` and the newest comment, which never touches the ticket's own row: what
   * the portal's "Última novedad" shows, on the card and on the detail alike (`AM-313`). The server
   * sends it in both, with no change to `eTag`; it is optional here only so that the fixtures of before
   * the field stay valid, and the portal reads it through `lastActivityOf`.
   */
  lastPublicActivityAt?: string;
  sla?: TicketSlaSummary | null;
  eTag: string;
  /** `AM-330`: the day the work is planned for or was done (`yyyy-MM-dd`); empty for a ticket opened any other way. */
  workDate?: string | null;
}

export interface IncidentDetailPayload {
  severity: string;
  symptoms?: string | null;
  diagnosis?: string | null;
  workaround?: string | null;
  rootCause?: string | null;
  restoredAt?: string | null;
  affectedUserCount?: number | null;
  /**
   * P049: the same tag `PATCH /tickets/{id}/incident-detail` checks, unlike
   * {@link ProblemDetailPayload} and {@link ChangeDetailPayload} - the console can edit the
   * incident's detail (`AM-300`) from the ticket it already read, with no extra call to fetch one.
   */
  eTag: string;
}

export interface ProblemDetailPayload {
  investigation?: string | null;
  rootCause?: string | null;
  workaround?: string | null;
  isKnownError: boolean;
  knownErrorPublishedAt?: string | null;
  permanentResolution?: string | null;
  owner?: EntityReference | null;
}

export interface ChangeDetailPayload {
  changeType: string;
  reason?: string | null;
  riskLevel: string;
  implementationPlan?: string | null;
  testPlan?: string | null;
  backoutPlan?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  implementationResult?: string | null;
  cabNotes?: string | null;
}

export interface TicketDetail extends TicketSummary {
  /** `manual`, `portal`, `email`, ... and `maintenance` for a ticket a maintenance plan opened (`AM-219`). */
  source: string;
  /**
   * The maintenance plan that opened this ticket (G2 of docs/research/planes-de-mantenimiento.md);
   * optional so the fixtures of before this feature stay valid; absent or `null`, no plan did.
   */
  maintenancePlan?: EntityReference | null;
  /**
   * The contract frozen on the ticket, as an id: `Tickets` sits below `Billing` in the matrix of
   * 00 §8.1, so the API cannot name it and the console resolves it apart (`AM-116` point 17).
   */
  contractId: string | null;
  description?: string | null;
  statusReason?: EntityReference | null;
  isPriorityManual: boolean;
  department?: EntityReference | null;
  affectedPerson?: EntityReference | null;
  service?: EntityReference | null;
  serviceOffering?: EntityReference | null;
  category?: EntityReference | null;
  impact?: EntityReference | null;
  urgency?: EntityReference | null;
  resolution?: string | null;
  acknowledgedAt?: string | null;
  firstResponseAt?: string | null;
  reopenedCount: number;
  incident?: IncidentDetailPayload | null;
  problem?: ProblemDetailPayload | null;
  change?: ChangeDetailPayload | null;
}

/**
 * An asset linked to a ticket (07 §4.7, 07 §4.12, `AM-128`), with enough of the asset's own fields
 * that the console does not chase a second call for the common case.
 */
export interface TicketAssetLink {
  assetId: string;
  role: TicketAssetRole;
  name: string;
  assetTag: string | null;
  serialNumber: string | null;
  status: AssetStatus;
  assetTypeId: string;
  organizationId: string;
  warrantyExpiresOn: string | null;
  linkedAt: string;
}

export interface WorkNoteDetail {
  id: string;
  ticketId: string;
  author: EntityReference;
  body: string;
  editedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  eTag: string;
}

export interface TicketAssignmentEntry {
  id: string;
  team?: EntityReference | null;
  agent?: EntityReference | null;
  assignedAt: string;
  unassignedAt?: string | null;
  assignedBy?: EntityReference | null;
  reason?: string | null;
}

/**
 * The types a person links two tickets with by hand. `replaces` (`AM-302`) is not among them: only
 * "Crear uno nuevo a partir de este" writes it, and the server refuses it from the link dialog
 * (`relationship_type_reserved`).
 */
export const LINKABLE_RELATIONSHIP_TYPES = [
  'related_to',
  'duplicate_of',
  'caused_by',
  'resolves',
  'blocks',
] as const;

export interface TicketRelationshipEntry {
  id: string;
  relationshipType: string;
  // P042: whether this row is seen from the ticket that did not declare the relationship, so a
  // non-symmetric type (duplicate_of, caused_by, resolves, blocks, replaces) reads with its inverse label.
  viewedFromTarget: boolean;
  ticket: EntityReference;
  note?: string | null;
  createdAt: string;
}

/**
 * A destination of `GET /tickets/{id}/transitions` (13 §4.3.1, `AM-147` point 1): an edge the
 * tenant declared out of the ticket's current status that this actor may actually walk, with what
 * the move will demand of it. The console narrows this list no further - the server applied the
 * very predicate the command applies (`AM-147` point 3) - and an empty list is the answer that the
 * actor may not move this ticket at all.
 */
export interface TicketTransitionOption {
  toStatusId: string;
  name: string;
  category: StatusCategory;
  requiresComment: boolean;
  requiresStatusReason: boolean;
  requiresResolution: boolean;
  /**
   * The reasons of the destination, empty unless it demands one (`AM-150` point 1). They travel
   * here because the only route that serves them, `GET /statuses/{id}/reasons`, takes
   * `admin.catalog.manage`: asking for them separately would leave the selector empty for the very
   * actor it is for, which is the defect `AM-147` closed for the destinations themselves.
   */
  reasons: TicketTransitionReason[];
}

/** One reason of a destination: what `statusReasonId` takes, with the tenant's own label. */
export interface TicketTransitionReason {
  id: string;
  name: string;
}

/** The two answers the requester gives a resolved ticket from the portal (09 §5.4, §5.8). */
export const TICKET_REQUESTER_ACTIONS = ['reopen', 'confirm'] as const;

export type TicketRequesterActionKind = (typeof TICKET_REQUESTER_ACTIONS)[number];

/**
 * One answer of `GET /tickets/{id}/requester-actions`: "Sigue sin resolverse" (`reopen`, always
 * with a comment) or "Confirmar solución" (`confirm`). The list is empty for anybody but the
 * requester and for a ticket that is not resolved, and an action is missing when the tenant's flow
 * has no edge for it: the portal shows exactly the buttons the command will accept.
 */
export interface TicketRequesterAction {
  action: TicketRequesterActionKind;
  toStatusId: string;
  requiresComment: boolean;
}

export interface TransitionTicketInput {
  toStatusId: string;
  statusReasonId?: string | null;
  resolution?: string | null;
  comment?: string | null;
}

export interface AssignTicketInput {
  assignedTeamId?: string | null;
  assignedAgentUserId?: string | null;
  reason?: string | null;
}

export interface LinkTicketInput {
  targetTicketId: string;
  relationshipType: string;
  note?: string | null;
}

/**
 * The moment the service came back, which closes the `restoration` objective (12 §2.2, 00 §5.16).
 * It is a command and never a field of the patch: the fact is sealed, not edited. Sending nothing
 * seals the server's own now.
 */
export interface RestoreTicketInput {
  restoredAt?: string | null;
}

/** A merge patch (RFC 7396). It never carries `statusId`: a state change is a command (`SM-02`). */
export type UpdateTicketInput = Record<string, unknown> & {
  title?: string;
  description?: string | null;
  serviceId?: string | null;
  serviceOfferingId?: string | null;
  categoryId?: string | null;
  impactId?: string | null;
  urgencyId?: string | null;
  priorityId?: string | null;
  /** `AM-213`. */
  locationId?: string | null;
  /** `AM-330`: `yyyy-MM-dd`; null clears the day. */
  workDate?: string | null;
};

/**
 * The merge patch of `PATCH /tickets/{id}/incident-detail` (`AM-300`): only what changed travels.
 * The four texts are trimmed by the server and an empty one is stored as `null`; the count is a
 * whole number, zero or more. It never carries `restoredAt`: the restoration is sealed by its own
 * command (`AM-143` point 1).
 */
export type UpdateIncidentDetailInput = Record<string, unknown> & {
  severity?: string;
  symptoms?: string | null;
  diagnosis?: string | null;
  workaround?: string | null;
  rootCause?: string | null;
  affectedUserCount?: number | null;
};

/**
 * The body of `POST /tickets`, the alta an agent fills at `/tickets/new` (13 §4.4, route table
 * line 226; `AM-156`). Three fields the request contract declares are deliberately absent:
 *
 * - `source`, because a ticket opened from the console is manual by definition and the use case
 *   writes `manual` when the field does not travel; asking for it would let the console claim the
 *   ticket came from the portal or from mail, which it did not.
 * - `priorityId` only travels from an actor holding `tickets.override_priority` - the use case
 *   refuses it otherwise - and leaving it out is precisely what lets the matrix derive the
 *   priority from impact and urgency (`PRI-01`).
 * - `severity`, `changeType`, `riskLevel`, `changeReason`, `serviceOfferingId` and
 *   `affectedPersonId` each have a server-side default or belong to the type's own detail block,
 *   which the workspace of `/tickets/:ticketId` already edits.
 *
 * `organizationId` and `requesterPersonId` are optional for the same reason: absent, the use case
 * fills them from the actor. The customer is the one worth choosing well, because the contract is
 * resolved and frozen when the ticket is born (00 §4.5).
 */
export interface CreateTicketInput {
  ticketType: TicketType;
  title: string;
  description?: string | null;
  organizationId?: string | null;
  departmentId?: string | null;
  requesterPersonId?: string | null;
  serviceId?: string | null;
  categoryId?: string | null;
  impactId?: string | null;
  urgencyId?: string | null;
  priorityId?: string | null;
  assignedTeamId?: string | null;
  assignedAgentUserId?: string | null;
  /** The agent's first choice (`AM-208`); absent, the service's rubro, else the tenant's default. */
  serviceAreaId?: string | null;
  /** `AM-213`: required when the chosen rubro's `requiresLocation` is true; optional otherwise. */
  locationId?: string | null;
  /** P049: only for `ticketType: 'incident'`; absent, the server's own default (Sev3). */
  severity?: string | null;
  /** `AM-330`: the day of the work (`yyyy-MM-dd`), which "Ticket rápido" asks; the alta sends none. */
  workDate?: string | null;
}

/**
 * Body of `POST /tickets/quick`, "Ticket rápido" (`AM-376`): a Pedido with the customer, what has to be
 * done and the day of the work. `time` records the work done as the caller's own hour, on that day and
 * on no task (`time_entries.create`); `finished` resolves the ticket in the same command
 * (`tickets.manage`), with `resolution` or, left empty, the title as its resolution. The server writes
 * all of it in one transaction or nothing.
 */
export interface QuickTicketInput {
  organizationId: string;
  title: string;
  workDate: string;
  /** `isBillable` left out: the server takes the "facturable por defecto" of the work type (`AM-382`). */
  time?: { durationMinutes: number; workTypeId: string; isBillable?: boolean };
  finished?: true;
  resolution?: string | null;
}

/**
 * The answer of `POST /tickets/quick`: the ticket as the command left it (in progress or resolved when
 * the hour or the resolution moved it), the hour with the warnings of its write when one was recorded,
 * and the warnings of the ticket's birth.
 */
export interface QuickTicketWritten {
  ticket: TicketDetail;
  time: TimeEntryWritten | null;
  warnings: string[];
}

/**
 * The answer of `POST /tickets/{id}/replacement` (`AM-302`): the new ticket, the one it replaced (now
 * cancelled), how many open tasks were copied, how many time entries moved to the new ticket and how
 * many minutes of work they add up to, and how many entries stayed on the original because they are
 * billed or inside a period. Entries are counted, not hours.
 */
export interface TicketReplacementResult {
  ticket: TicketDetail;
  replaced: EntityReference;
  tasksCopied: number;
  timeEntriesMoved: number;
  timeMinutesMoved: number;
  timeEntriesKept: number;
  warnings: string[];
}

export const ticketsApi = {
  list: (params: ListParams, signal?: AbortSignal): Promise<PageResponse<TicketSummary>> =>
    fetchPage<TicketSummary>('/api/v1/tickets', params, signal),
  get: (id: string, signal?: AbortSignal): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}`, signal === undefined ? {} : { signal }),
  /** `tickets.create` (13 route table line 226): answers `201` with the detail of the new ticket. */
  create: (body: CreateTicketInput): Promise<TicketDetail> =>
    apiFetch<TicketDetail>('/api/v1/tickets', { method: 'POST', body }),
  /**
   * "Ticket rápido" (`AM-376`, `tickets.create`): answers `201`. `idempotencyKey` (`AM-18`): the same key
   * with the same body replays the first answer, so a retry after a lost answer opens no second ticket.
   */
  quickCreate: (body: QuickTicketInput, idempotencyKey: string): Promise<QuickTicketWritten> =>
    apiFetch<QuickTicketWritten>('/api/v1/tickets/quick', {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  update: (id: string, body: UpdateTicketInput, etag: string): Promise<TicketDetail> =>
    patchResource<TicketDetail>(`/api/v1/tickets/${id}`, body, etag),
  /**
   * `POST /tickets/{id}/move-area` (`AM-208`, `tickets.manage`): clears the service, the offering
   * and the category when they do not belong to the destination, and the type has to be allowed
   * there; assignment, priority and the SLA already frozen stay as they were.
   */
  moveArea: (id: string, serviceAreaId: string, etag: string): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/move-area`, {
      method: 'POST',
      body: { serviceAreaId },
      headers: { 'If-Match': etag },
    }),
  /**
   * The transition selector of zone A. It replaces the two catalogue collections the modal used to
   * read: both take `admin.catalog.manage`, which no agent holds, so the selector was empty for
   * exactly the actor it is for (`AM-147` point 4).
   */
  transitions: async (id: string, signal?: AbortSignal): Promise<TicketTransitionOption[]> =>
    (
      await apiFetch<{ items: TicketTransitionOption[] }>(
        `/api/v1/tickets/${id}/transitions`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  transition: (id: string, body: TransitionTicketInput, etag: string): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/transition`, {
      method: 'POST',
      body,
      headers: { 'If-Match': etag },
    }),
  /** What the requester may answer to the resolved ticket right now (portal). */
  requesterActions: async (id: string, signal?: AbortSignal): Promise<TicketRequesterAction[]> =>
    (
      await apiFetch<{ items: TicketRequesterAction[] }>(
        `/api/v1/tickets/${id}/requester-actions`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  /**
   * `POST /tickets/{id}/requester-reopen` or `/requester-confirm`: the requester's own path, which
   * takes neither `tickets.reopen` nor `tickets.close`. The comment is written as the customer's.
   */
  answerAsRequester: (
    id: string,
    action: TicketRequesterActionKind,
    comment: string | null,
    etag: string,
  ): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/requester-${action}`, {
      method: 'POST',
      body: { comment },
      headers: { 'If-Match': etag },
    }),
  assign: (id: string, body: AssignTicketInput, etag: string): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/assign`, {
      method: 'POST',
      body,
      headers: { 'If-Match': etag },
    }),
  restore: (id: string, body: RestoreTicketInput, etag: string): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/restore`, {
      method: 'POST',
      body,
      headers: { 'If-Match': etag },
    }),
  /** P049, `AM-300`: `tickets.manage`; the tag is the incident detail's own (`incident.eTag`), not the ticket's. */
  updateIncidentDetail: (
    id: string,
    body: UpdateIncidentDetailInput,
    etag: string,
  ): Promise<IncidentDetailPayload> =>
    patchResource<IncidentDetailPayload>(`/api/v1/tickets/${id}/incident-detail`, body, etag),
  /**
   * "Crear uno nuevo a partir de este" (`AM-302`, `tickets.manage` and `tickets.create`): the body is
   * the alta's, with the organization chosen again. In one transaction the server opens the new
   * ticket, copies the open tasks, moves the hours that are not billed nor inside a period, cancels
   * the original with the reason "Reemplazado por otro ticket" and links both. `etag` is the
   * original's.
   */
  replace: (id: string, body: CreateTicketInput, etag: string): Promise<TicketReplacementResult> =>
    apiFetch<TicketReplacementResult>(`/api/v1/tickets/${id}/replacement`, {
      method: 'POST',
      body,
      headers: { 'If-Match': etag },
    }),
  link: (id: string, body: LinkTicketInput): Promise<TicketDetail> =>
    apiFetch<TicketDetail>(`/api/v1/tickets/${id}/relationships`, { method: 'POST', body }),
  listComments: async (id: string, signal?: AbortSignal): Promise<CommentDetail[]> =>
    (
      await apiFetch<{ items: CommentDetail[] }>(
        `/api/v1/tickets/${id}/comments`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  addComment: (id: string, body: string): Promise<CommentDetail> =>
    apiFetch<CommentDetail>(`/api/v1/tickets/${id}/comments`, { method: 'POST', body: { body } }),
  /** Never requested without `work_notes.view`: the server refuses it and the console does not offer it. */
  listWorkNotes: async (id: string, signal?: AbortSignal): Promise<WorkNoteDetail[]> =>
    (
      await apiFetch<{ items: WorkNoteDetail[] }>(
        `/api/v1/tickets/${id}/work-notes`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  addWorkNote: (id: string, body: string): Promise<WorkNoteDetail> =>
    apiFetch<WorkNoteDetail>(`/api/v1/tickets/${id}/work-notes`, {
      method: 'POST',
      body: { body },
    }),
  listAssignments: async (id: string, signal?: AbortSignal): Promise<TicketAssignmentEntry[]> =>
    (
      await apiFetch<{ items: TicketAssignmentEntry[] }>(
        `/api/v1/tickets/${id}/assignments`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  listRelationships: async (id: string, signal?: AbortSignal): Promise<TicketRelationshipEntry[]> =>
    (
      await apiFetch<{ items: TicketRelationshipEntry[] }>(
        `/api/v1/tickets/${id}/relationships`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  /**
   * The files of the ticket (`AM-165`). `07` §4.7 offers them as `expand=attachments`, which this
   * product implements nowhere: every related read of a ticket is a sub-resource, the way
   * `/comments`, `/work-notes` and `/audit-logs` already are. It takes `attachments.view` plus the
   * ticket's own record rule, so the console asks for it only when the session holds that key.
   */
  listAttachments: async (id: string, signal?: AbortSignal): Promise<AttachmentDetail[]> =>
    (
      await apiFetch<{ items: AttachmentDetail[] }>(
        `/api/v1/tickets/${id}/attachments`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  /** The selector of 05e §2.3 offers assets, never configuration items (`AM-128`). */
  assets: async (id: string, signal?: AbortSignal): Promise<TicketAssetLink[]> =>
    (
      await apiFetch<{ items: TicketAssetLink[] }>(
        `/api/v1/tickets/${id}/assets`,
        signal === undefined ? {} : { signal },
      )
    ).items,
  /** Replaces the whole set (07 §1.3, §4.7): the caller sends what the ticket should carry after the call. */
  replaceAssets: (
    id: string,
    items: Array<{ assetId: string; role: TicketAssetRole }>,
  ): Promise<TicketAssetLink[]> =>
    apiFetch<{ items: TicketAssetLink[] }>(`/api/v1/tickets/${id}/assets`, {
      method: 'PUT',
      body: { items },
    }).then((page) => page.items),
  /** `GET /assets/{id}/tickets` (07 §4.12): the same page `GET /tickets` answers, scoped to one asset. */
  byAsset: (
    assetId: string,
    params: ListParams,
    signal?: AbortSignal,
  ): Promise<PageResponse<TicketSummary>> =>
    fetchPage<TicketSummary>(`/api/v1/assets/${assetId}/tickets`, params, signal),
};

/** Converts an approved request into a ticket, or links it to one already open (00 §3.5). */
export function convertRequest(
  requestId: string,
  body: { ticketType?: string | null; ticketId?: string | null },
): Promise<TicketDetail> {
  return apiFetch<TicketDetail>(`/api/v1/requests/${requestId}/convert`, {
    method: 'POST',
    body,
  });
}
